;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:pathArray*\",\"regex\":\"^/(.+)$\",\"paramNames\":[\"pathArray\"],\"hasOptionalPrefix\":false},{\"path\":\"/customer/:pathArray*\",\"regex\":\"^/customer/(.+)$\",\"paramNames\":[\"pathArray\"],\"hasOptionalPrefix\":false},{\"path\":\"/swatches/swatch/:fabricName*\",\"regex\":\"^/swatches/swatch/(.+)$\",\"paramNames\":[\"fabricName\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/swatches\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.0.9";import * as Sentry from '@sentry/nextjs';

import getEnvValue from 'commons/getEnvValue';

// Load public environment variables (populate globalThis._env_)
import './publicEnvConfig';

import { logInfo } from 'commons/logger';
import sharedSentryConfig, {
  isNonErrorPromiseRejection,
} from '../sentry.shared-config';
import initDash0WebSdk from './initDash0WebSdk';
import { takePendingRecoveryReport } from './utils/chunkLoadRecovery';

// Sentry JOYBIRD-40-5PE1 — "removeChild"/"parentNode is null" React commit-phase
// crash. Two coupled causes: (1) trigger — browser auto-translate (~88% of
// affected *users* are Chromium; Google Translate wraps text nodes React owns in
// <font> tags); (2) the volume spike is a regression deployed 2026-06-17 on the
// Algolia category/PLP pages, whose SSR-rendered product grid is re-run and
// swapped on the client — React then deletes a subtree translate has mutated and
// calls removeChild/insertBefore against a parent it no longer controls, throwing
// to window.onerror and re-firing thousands of times per session.
//
// This is the MITIGATION (React #11538 guard): make both DOM ops tolerant of the
// parent mismatch so React's commit phase cannot crash. It does NOT fix the
// underlying grid-swap regression (tracked separately) — the page may still be
// mis-translated, it just won't crash. Runs in instrumentation-client (Next's
// earliest client hook) so the guards are installed before React hydrates. In-app
// DOM ops are unchanged — the guards only no-op / fall back when the node's parent
// is not what the caller expects.
if (typeof window !== 'undefined' && typeof Node !== 'undefined') {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return originalInsertBefore.call(this, newNode, null);
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  };
}

const isLocal = getEnvValue('NEXT_PUBLIC_ENV_NAME') === 'local';

// Sticky per-session sampling decision shared across Sentry and Dash0.
// The Dash0 Web SDK does not expose a sample-rate option, so we sample by
// gating its init() instead. We make the same decision for Sentry so a
// session is either fully sampled across both tools or fully dropped.
// See .github/instructions/tracing.instructions.md.
const SESSION_SAMPLE_KEY = '__joybird_trace_sampled';

const parseSampleRate = value => {
  const parsed = parseFloat(value ?? '');
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(1, Math.max(0, parsed));
};

const computeSessionSampleDecision = sampleRate => {
  try {
    const stored = window.sessionStorage.getItem(SESSION_SAMPLE_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
    const decision = Math.random() < sampleRate;
    window.sessionStorage.setItem(SESSION_SAMPLE_KEY, decision ? '1' : '0');
    return decision;
  } catch {
    return Math.random() < sampleRate;
  }
};

const tracingSampleRate = parseSampleRate(
  getEnvValue('NEXT_PUBLIC_TRACING_SAMPLE_RATE') || '0.001'
);
const isTraceSampled = computeSessionSampleDecision(tracingSampleRate);

const replaySessionSampleRate = parseSampleRate(
  getEnvValue('NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE') || '0.001'
);
const replayOnErrorSampleRate = parseSampleRate(
  getEnvValue('NEXT_PUBLIC_SENTRY_REPLAYS_ERROR_SAMPLE_RATE') || '0.001'
);

// Initialize Sentry on the client
// https://docs.sentry.io/platforms/javascript/guides/nextjs/manual-setup/
Sentry.init({
  ...sharedSentryConfig,

  // Override the shared tracesSampleRate with a per-session decision so the
  // sampling outcome agrees with the Dash0 init gate below. Sentry uses
  // tracesSampler in preference to tracesSampleRate when both are present.
  tracesSampler: () => (isTraceSampled ? 1 : 0),

  // Proxy envelopes through our server to bypass ad blockers and avoid CORS issues.
  // Only needed locally — in deployed environments Sentry requests go direct.
  ...(isLocal && { tunnel: '/api/sentry-tunnel/' }),

  // Client-specific integrations (Replay is lazy-loaded below — CON-8282)
  integrations: [
    // False positive: the plugin cannot traverse @sentry/nextjs's re-export
    // chain, but this export exists at runtime.
    // eslint-disable-next-line import/namespace
    Sentry.browserTracingIntegration({
      enableLongTask: true,
      enableNavigationSpan: true,
    }),
  ],

  // Replay sample rates must be set at init even though the integration loads later.
  // Vars must be prefixed NEXT_PUBLIC_ so they reach the client via globalThis._env_
  // (see packages/consumer/src/getPublicEnvVars.mjs).
  replaysSessionSampleRate: replaySessionSampleRate,
  replaysOnErrorSampleRate: replayOnErrorSampleRate,

  // Trace propagation targets - match the same domains as initDash0WebSdk propagators.
  // Exclude third-party analytics subdomains starting with 'gtm' or 'rudr'
  // (e.g. gtm.prod.nest.joybird.com) to prevent CORS errors.
  // [^/?#]* restricts matching to the hostname only, preventing false positives
  // when joybird.com appears in query parameters of third-party URLs.
  tracePropagationTargets: [
    /^https:\/\/(?!(?:gtm|rudr))[^/?#]*\.joybird\.com/,
    /^https?:\/\/[^/?#]*\.local/,
    /^https?:\/\/localhost/,
    getEnvValue('NEXT_PUBLIC_GRAPHQL_BASE_URL'),
    process.env.PRIVATE_GRAPHQL_BASE_URL,
  ].filter(Boolean),

  // Client-specific error handling
  beforeSend(event, hint) {
    // Call shared beforeSend first
    const filteredEvent = sharedSentryConfig.beforeSend?.(event, hint);
    if (!filteredEvent) return null;

    // Additional client-side filtering
    if (event.exception && isNonErrorPromiseRejection(event, hint)) {
      return null;
    }

    if (event.exception) {
      const error = hint.originalException;
      // CON-8173: When a GraphQL request is aborted (navigation away/tab close),
      // our Apollo fetch wrapper returns a synthetic Response(499) on AbortError
      // (see src/app/_root/_apollo-client/makeApolloClient.js). Apollo surfaces
      // this as "Response not successful: Received status code 499", which is
      // non-actionable noise.
      if (error?.networkError?.statusCode === 499) {
        return null;
      }
      if (error?.message) {
        const { message } = error;
        if (
          message.includes('ResizeObserver loop limit exceeded') ||
          message.includes('Response not successful: Received status code 499')
        ) {
          return null;
        }
      }
    }
    return filteredEvent;
  },
});

// JOYBIRD-40-5QA6 — recovered failures are not reported as errors, so count them
// here or the issue goes quiet regardless. Multiply the count by 1 / the rate.
const RECOVERY_REPORT_SAMPLE_RATE = 0.1;

const pendingRecovery = sharedSentryConfig.enabled
  ? takePendingRecoveryReport()
  : null;
if (pendingRecovery && Math.random() < RECOVERY_REPORT_SAMPLE_RATE) {
  Sentry.captureMessage('Chunk load recovered by reload', {
    level: 'info',
    tags: {
      chunkLoadOrigin: pendingRecovery.origin,
      chunkLoadRecoveryAttempt: String(pendingRecovery.attempt),
      chunkLoadRecoverySampleRate: String(RECOVERY_REPORT_SAMPLE_RATE),
    },
  });
}

// Lazy-load Session Replay after the critical path (CON-8282). Dynamic import
// keeps ~350KB of replay code out of the first-load bundle; sample rates above
// still apply once the integration is added.
// https://docs.sentry.io/platforms/javascript/session-replay/#lazy-loading-replay
if (
  sharedSentryConfig.enabled &&
  (replaySessionSampleRate > 0 || replayOnErrorSampleRate > 0)
) {
  const loadReplayIntegration = () => {
    import('@sentry/browser')
      .then(({ replayIntegration }) => {
        Sentry.addIntegration(
          replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            mask: ['.sensitive', '[data-sensitive]'],
          })
        );
      })
      .catch(() => {
        // Replay is optional telemetry; swallow load failures (ad blockers, etc.).
      });
  };

  if (typeof window.requestIdleCallback === 'function') {
    // Schedule immediately so early errors/interactions are captured when
    // replaysOnErrorSampleRate is enabled; idle keeps replay off the hot path.
    window.requestIdleCallback(loadReplayIntegration, { timeout: 5000 });
  } else {
    // No native requestIdleCallback (Safari) — this file runs before the
    // polyfill in LoadClientPolyfills.js. Wait for load so replay stays off
    // the critical path; first-load contention is largely past by then.
    const loadAfterWindowLoad = () => loadReplayIntegration();

    if (document.readyState === 'complete') {
      loadAfterWindowLoad();
    } else {
      window.addEventListener('load', loadAfterWindowLoad, { once: true });
    }
  }
}

const isClientTracingEnabled =
  getEnvValue('NEXT_PUBLIC_CLIENT_TRACING_ENABLED') === 'true';

if (isClientTracingEnabled && isTraceSampled) {
  logInfo('Initializing Dash0 Web SDK on client');
  initDash0WebSdk();
} else if (!isClientTracingEnabled) {
  logInfo('Dash0 Web SDK is disabled');
} else {
  logInfo(
    `Dash0 Web SDK skipped for this session (sample rate ${tracingSampleRate})`
  );
}

// False positive: the plugin cannot traverse @sentry/nextjs's re-export chain,
// but this export exists at runtime.
// eslint-disable-next-line import/namespace
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
